import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getDrivesByAgent, getDocumentsByAgent, listDocuments } from '../../../../services';
import {
  parseAgentDocumentsListFromResponse,
  parseAgentDrivesListFromResponse,
  parseTenantDocumentListFromResponse,
} from '../../../../shared/utils/apiResponse';
import { useAgentKnowledgeEnv } from './AgentKnowledgeEnvContext.jsx';

const AgentKnowledgeDrivesContext = createContext(null);

export function AgentKnowledgeDrivesProvider({ children }) {
  const { agentId, vaultUuid } = useAgentKnowledgeEnv();

  const [drives, setDrives] = useState([]);
  const [drivesLoading, setDrivesLoading] = useState(false);
  const [drivesError, setDrivesError] = useState(null);
  const [drivesFetched, setDrivesFetched] = useState(false);

  const [agentDocuments, setAgentDocuments] = useState([]);
  const [agentDocumentsLoading, setAgentDocumentsLoading] = useState(false);
  const [agentDocumentsError, setAgentDocumentsError] = useState(null);
  const [agentDocumentsFetched, setAgentDocumentsFetched] = useState(false);

  const previousAgentIdRef = useRef(agentId);
  const previousVaultUuidRef = useRef(vaultUuid);

  const fetchDrives = useCallback(
    async (force = false) => {
      if (!agentId) return;
      if (drivesFetched && !force) return;

      setDrivesLoading(true);
      setDrivesError(null);

      try {
        const response = await getDrivesByAgent(agentId);
        let drivesList = parseAgentDrivesListFromResponse(response);
        if (vaultUuid) {
          drivesList = drivesList.filter(
            (drive) => (drive.vaultUuid || drive.uuid || drive.id) !== vaultUuid,
          );
        }
        setDrives(drivesList);
        setDrivesFetched(true);
      } catch (err) {
        setDrivesError(err);
      } finally {
        setDrivesLoading(false);
      }
    },
    [agentId, vaultUuid, drivesFetched],
  );

  const addDrive = useCallback((drive) => {
    setDrives((prev) => [...prev, drive]);
  }, []);

  const removeDrive = useCallback((driveId) => {
    setDrives((prev) =>
      prev.filter((d) => (d.vaultUuid || d.uuid || d.id) !== driveId),
    );
  }, []);

  const fetchAgentDocuments = useCallback(
    async (force = false) => {
      if (!agentId) return;
      if (agentDocumentsFetched && !force) return;

      setAgentDocumentsLoading(true);
      setAgentDocumentsError(null);

      try {
        const response = await getDocumentsByAgent(agentId);
        let documentsList = parseAgentDocumentsListFromResponse(response);

        let vaultDocumentUuids = new Set();
        if (vaultUuid) {
          try {
            const vaultResponse = await listDocuments({
              vault_uuid: vaultUuid,
              page_size: 100,
            });
            const vaultDocs = parseTenantDocumentListFromResponse(vaultResponse);
            vaultDocumentUuids = new Set(
              vaultDocs
                .map((doc) => doc.documentUuid ?? doc.document_uuid ?? doc.uuid ?? doc.id)
                .filter(Boolean),
            );
          } catch {
            /* non-fatal */
          }
        }

        if (vaultDocumentUuids.size > 0) {
          documentsList = documentsList.filter(
            (doc) =>
              !vaultDocumentUuids.has(doc.uuid) &&
              !vaultDocumentUuids.has(doc.documentUuid) &&
              !vaultDocumentUuids.has(doc.document_uuid),
          );
        }

        setAgentDocuments(documentsList);
        setAgentDocumentsFetched(true);
      } catch (err) {
        setAgentDocumentsError(err);
      } finally {
        setAgentDocumentsLoading(false);
      }
    },
    [agentId, vaultUuid, agentDocumentsFetched],
  );

  const addAgentDocument = useCallback((document) => {
    setAgentDocuments((prev) => [...prev, document]);
  }, []);

  const removeAgentDocument = useCallback((documentId) => {
    setAgentDocuments((prev) =>
      prev.filter((d) => (d.uuid || d.id) !== documentId),
    );
  }, []);

  const fetchAll = useCallback(
    async (force = false) => {
      await Promise.all([fetchDrives(force), fetchAgentDocuments(force)]);
    },
    [fetchDrives, fetchAgentDocuments],
  );

  const refetch = useCallback(() => fetchAll(true), [fetchAll]);

  const reset = useCallback(() => {
    setDrives([]);
    setDrivesLoading(false);
    setDrivesError(null);
    setDrivesFetched(false);
    setAgentDocuments([]);
    setAgentDocumentsLoading(false);
    setAgentDocumentsError(null);
    setAgentDocumentsFetched(false);
  }, []);

  useEffect(() => {
    const prevAgent = previousAgentIdRef.current;
    const prevVault = previousVaultUuidRef.current;
    if (prevAgent !== agentId || prevVault !== vaultUuid) {
      previousAgentIdRef.current = agentId;
      previousVaultUuidRef.current = vaultUuid;
      reset();
      if (agentId) {
        void fetchAll(true);
      }
    }
  }, [agentId, vaultUuid, reset, fetchAll]);

  const loading = drivesLoading || agentDocumentsLoading;
  const error = drivesError || agentDocumentsError;
  const hasFetched = drivesFetched && agentDocumentsFetched;

  const value = useMemo(
    () => ({
      drives,
      drivesLoading,
      drivesError,
      drivesFetched,
      fetchDrives,
      addDrive,
      removeDrive,
      agentDocuments,
      agentDocumentsLoading,
      agentDocumentsError,
      agentDocumentsFetched,
      fetchAgentDocuments,
      addAgentDocument,
      removeAgentDocument,
      loading,
      error,
      hasFetched,
      drivesCount: drives.length,
      agentDocumentsCount: agentDocuments.length,
      totalCount: drives.length + agentDocuments.length,
      fetchAll,
      refetch,
      reset,
    }),
    [
      drives,
      drivesLoading,
      drivesError,
      drivesFetched,
      fetchDrives,
      addDrive,
      removeDrive,
      agentDocuments,
      agentDocumentsLoading,
      agentDocumentsError,
      agentDocumentsFetched,
      fetchAgentDocuments,
      addAgentDocument,
      removeAgentDocument,
      loading,
      error,
      hasFetched,
      fetchAll,
      refetch,
      reset,
      // drivesCount / agentDocumentsCount are derived and intentionally excluded
      // from deps since they're computed inline above.
    ],
  );

  return (
    <AgentKnowledgeDrivesContext.Provider value={value}>
      {children}
    </AgentKnowledgeDrivesContext.Provider>
  );
}

export function useAgentKnowledgeDrives() {
  const ctx = useContext(AgentKnowledgeDrivesContext);
  if (!ctx) {
    throw new Error('useAgentKnowledgeDrives must be used within AgentKnowledgeDrivesProvider');
  }
  return ctx;
}

export default AgentKnowledgeDrivesContext;
